import { PanelType } from '@editor/config/constants'
import {
  BLUEPRINT_3D_DEFAULT_BACKGROUND,
  BLUEPRINT_DEFAULT_PCD_TRANSPARENCY,
  EDITOR_DEFAULT_POINT_SIZE,
} from '@editor/config/styles'
import { PointArray } from '@editor/interfaces/attribute'
import { BlueprintV2 } from '@editor/interfaces/blueprint'
import { BlueprintPermissionSet } from '@editor/interfaces/validation'
import BlueprintToolTextAnnotation from '@editor/pages/projects/blueprint-editor/tools/TextAnnotation/store'
import { getBlueprints } from '@editor/services/Blueprint'
import { ERROR_PROCESS } from '@editor/services/ErrorHandler'
import { GET_INSPECTION_AREA_API_URL } from '@editor/services/InspectionArea'
import { withStandardHandlers } from '@editor/services/Util'
import {
  deleteStateFromLocalStorage,
  loadStateFromLocalStorage,
  saveStateToLocalStorage,
} from '@editor/services/localStorage'
import { PayloadAction, combineReducers, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import mixpanel from 'mixpanel-browser'

import { BlueprintEditorTools, Mode } from '../definitions'
import { getDefaultSelectedBlueprint } from '../utils'
import drawingReducer from './drawings'

/**
 * Fetch blueprints for a specific project and area.
 *
 * @param accessToken Auth0 access token
 * @param projectId Project ID
 * @param areaId Area ID
 */
export const fetchBlueprints = createAsyncThunk(
  'blueprintEditor/fetchBlueprints',
  async ({ accessToken, projectId, areaId }: { accessToken: string; projectId: string; areaId: string }) =>
    getBlueprints(accessToken, projectId, areaId),
)

/**
 * Update a blueprint in the inspection area.
 *
 * TODO: When backend supports print_scale field, make these changes:
 * 1. Line 70: Uncomment `print_scale: _print_scale` in the API request body
 * 2. Line 500-511: Replace print_scale preservation logic with simple `const updatedBlueprint = payload`
 * 3. usePrintScale.ts line 76-79: Remove the MOCK console.warn about backend support
 *
 * After these changes, print_scale will flow normally: Frontend -> Backend -> Response -> Redux -> UI
 */
export const updateBlueprint = createAsyncThunk(
  'blueprintEditor/updateBlueprint',
  async ({
    accessToken,
    projectId,
    areaId,
    blueprint: {
      blueprint_id,
      name,
      masking_region,
      drawings,
      inspection_items,
      polygons,
      cylinders,
      print_scale: _print_scale,
    },
    updateMaskingRegion = false,
  }: {
    accessToken: string
    projectId: string
    areaId: string
    blueprint: Partial<BlueprintV2>
    updateMaskingRegion?: boolean
  }) => {
    const fn = () =>
      fetch(`${GET_INSPECTION_AREA_API_URL(projectId, areaId)}/blueprints-v2/${blueprint_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          responseType: 'json',
          'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name,
          ...(updateMaskingRegion ? { masking_region } : {}),
          drawings,
          inspection_items,
          polygons,
          cylinders,
          print_scale: _print_scale,
        }),
      })

    return withStandardHandlers<typeof fn, BlueprintV2>(fn, ERROR_PROCESS.UPDATE_BLUEPRINT)()
  },
)

/**
 * Delete a blueprint from the inspection area.
 *
 * @param accessToken Auth0 access token
 * @param projectId Project ID
 * @param areaId Area ID
 * @param blueprintId Blueprint ID to be deleted
 */
export const deleteBlueprint = createAsyncThunk(
  'blueprintEditor/deleteBlueprint',
  async (
    {
      accessToken,
      projectId,
      areaId,
      blueprintId,
    }: {
      accessToken: string
      projectId: string
      areaId: string
      blueprintId: string
    },
    thunkApi,
  ) => {
    const fn = () =>
      fetch(`${GET_INSPECTION_AREA_API_URL(projectId, areaId)}/blueprints-v2/${blueprintId}`, {
        method: 'DELETE',
        headers: {
          responseType: 'json',
          'X-Authorization': `Bearer ${accessToken}`,
        },
      })

    thunkApi.dispatch(addDeletingBlueprintId(blueprintId))
    return withStandardHandlers<typeof fn, void>(fn, ERROR_PROCESS.DELETE_BLUEPRINT)()
  },
)

export interface BlueprintEditorStore {
  /**
   * Blueprints loading state.
   */
  isLoading: boolean

  /**
   * User is allowed to do modification for editor-related actions
   */
  permissionSet: BlueprintPermissionSet

  /**
   * Whether the user is allowed to modify blueprints.
   */
  isAllowedToModify: boolean

  /**
   * The currently selected tool in the blueprint editor.
   */
  selectedTool: BlueprintEditorTools

  /**
   * The previously selected tool in the blueprint editor.
   * This is used to determine if the user has switched tools.
   */
  prevSelectedTool?: BlueprintEditorTools

  /**
   * The current mode of the blueprint editor.
   */
  mode: Mode

  /**
   * Whether the editor has completed its initial setup.
   */
  initCompleted: boolean

  /**
   * Editor load start time for performance tracking
   */
  editorLoadStartTime: number

  /**
   * Flag to indicate if the 2D point cloud is currently loading.
   */
  isPointCloud2dLoading: boolean

  /**
   * Bounding box of the 3D PCD.
   * Consists of 2 points, min and max.
   */
  pcdBoundingBox?: [PointArray, PointArray]

  /**
   * Bounding box of the 2D flattened/projected PCD.
   * Consists of 2 points, min and max.
   */
  pcd2dBoundingBox?: [PointArray, PointArray]

  /**
   * Bounding sphere radius of the PCD.
   */
  pcdBoundingSphereRadius: number

  /**
   * Whether the editor is currently creating a blueprint.
   */
  isCreatingBlueprint: boolean

  /**
   * List of collapsed panels.
   */
  collapsedPanel: PanelType[]

  /**
   * Whether the point cloud is invisible.
   */
  isPointCloudInvisible: boolean

  /**
   * Default point size for PCD.
   */
  defaultPointSize: number

  /**
   * Current point size for PCD.
   * This reflects the active setting, potentially derived from the loaded PCD.
   */
  pointSize: number

  /**
   * PCD transparency, normalized (0 to 1).
   */
  pcdTransparency: number

  /**
   * Background color for the main canvas.
   */
  backgroundColor: string

  /**
   * Whether the editor is currently batch saving.
   */
  isBatchSaving: boolean

  /**
   * List of blueprints available in the editor.
   */
  blueprints: BlueprintV2[]

  /**
   * Currently selected blueprint in the editor.
   */
  selectedBlueprint?: BlueprintV2

  /**
   * The previous selected blueprint before the current one.
   */
  previousSelectedBlueprintId?: string

  /**
   * IDs of the blueprint being deleted.
   * Array since multiple blueprints can be deleted at once.
   */
  deletingBlueprintIds: string[]

  /**
   * Opacity of the grid on the blueprint canvas (0-1 range).
   */
  gridOpacity: number
}

const initialState: BlueprintEditorStore = {
  isLoading: false,
  isPointCloud2dLoading: true,
  permissionSet: {
    BROWSE: [],
    MODIFY: [],
  },
  isAllowedToModify: false,

  initCompleted: false,
  editorLoadStartTime: 0,
  isBatchSaving: false,
  isCreatingBlueprint: false,

  selectedTool: BlueprintEditorTools.Selector,
  mode: Mode.Editor2D,
  isPointCloudInvisible: false,
  collapsedPanel: [PanelType.Settings],

  pcdBoundingSphereRadius: 1,

  defaultPointSize: EDITOR_DEFAULT_POINT_SIZE,
  pointSize: EDITOR_DEFAULT_POINT_SIZE,
  pcdTransparency: loadStateFromLocalStorage('blueprintPcdTransparency', BLUEPRINT_DEFAULT_PCD_TRANSPARENCY),
  backgroundColor: loadStateFromLocalStorage('blueprintBackgroundColor', BLUEPRINT_3D_DEFAULT_BACKGROUND),

  blueprints: [],
  selectedBlueprint: undefined,
  deletingBlueprintIds: [],

  gridOpacity: loadStateFromLocalStorage('blueprintGridOpacity', 0.5),
}

const slice = createSlice({
  name: 'projects/blueprint-editor',
  initialState,
  reducers: {
    setSelectedTool: (state, { payload }: PayloadAction<BlueprintEditorTools>) => {
      state.prevSelectedTool = state.selectedTool
      state.selectedTool = payload
    },
    setPrevSelectedTool: (state, { payload }: PayloadAction<BlueprintEditorTools | undefined>) => {
      state.prevSelectedTool = payload
    },
    setMode: (state, { payload }: PayloadAction<Mode>) => {
      state.mode = payload
      state.pcdTransparency = loadStateFromLocalStorage('blueprintPcdTransparency', 0.5)
    },
    setIsPointCloudInvisible: (state, { payload }: PayloadAction<boolean>) => {
      state.isPointCloudInvisible = payload
    },
    setGridOpacity: (state, { payload }: PayloadAction<number>) => {
      state.gridOpacity = payload
      saveStateToLocalStorage('blueprintGridOpacity', payload)
    },
    setInitCompleted: (state, { payload }: PayloadAction<boolean>) => {
      state.initCompleted = payload

      // Track Blueprint Editor Load Time when initialization is completed
      if (payload) {
        const loadTime = Date.now() - state.editorLoadStartTime
        if (state.editorLoadStartTime > 0) {
          mixpanel.track('Blueprint Editor Load Time', {
            Duration: Math.round(loadTime / 1000), // in seconds
            'Duration (ms)': loadTime, // in milliseconds
          })
        }
      }
    },
    setIsPointCloud2dLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isPointCloud2dLoading = payload
    },
    setIsBatchSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.isBatchSaving = payload
    },
    setPcdBoundingBox: (state, { payload }: PayloadAction<[PointArray, PointArray] | undefined>) => {
      state.pcdBoundingBox = payload
    },
    setPcd2dBoundingBox: (state, { payload }: PayloadAction<[PointArray, PointArray] | undefined>) => {
      state.pcd2dBoundingBox = payload
    },
    setPcdBoundingSphereRadius: (state, { payload }: PayloadAction<number>) => {
      state.pcdBoundingSphereRadius = payload
    },
    setEditorLoadStartTime: (state, { payload }: PayloadAction<number>) => {
      state.editorLoadStartTime = payload
    },

    /**
     * Permissions
     */
    setPermissionSet: (state, action: PayloadAction<BlueprintPermissionSet>) => {
      state.permissionSet = action.payload
    },
    setIsAllowedModify: (state, { payload }: PayloadAction<boolean>) => {
      state.isAllowedToModify = payload
    },

    /**
     * Blueprints
     */
    addBlueprint: (state, { payload }: PayloadAction<BlueprintV2>) => {
      state.blueprints.push(payload)
    },
    removeBlueprint: (state, { payload }: PayloadAction<string>) => {
      state.blueprints = state.blueprints.filter((blueprint) => blueprint.blueprint_id !== payload)
      if (state.selectedBlueprint?.blueprint_id === payload) {
        state.selectedBlueprint = getDefaultSelectedBlueprint(state.blueprints)

        if (state.previousSelectedBlueprintId === payload) {
          state.previousSelectedBlueprintId = undefined
        }
      }
    },
    setBlueprints: (state, { payload }: PayloadAction<BlueprintV2[]>) => {
      state.blueprints = payload
    },
    updateBlueprints: (state, { payload }: PayloadAction<BlueprintV2[]>) => {
      state.blueprints = state.blueprints.map((blueprint) => {
        const updatedBlueprint = payload.find((b) => b.blueprint_id === blueprint.blueprint_id)
        if (updatedBlueprint) {
          return updatedBlueprint
        }
        return blueprint
      })

      if (state.selectedBlueprint) {
        const updatedBlueprint = payload.find((b) => b.blueprint_id === state.selectedBlueprint!.blueprint_id)
        if (updatedBlueprint) {
          state.selectedBlueprint = updatedBlueprint
        }
      }
    },
    setSelectedBlueprint: (state, { payload }: PayloadAction<BlueprintV2 | undefined>) => {
      if (state.selectedBlueprint?.blueprint_id) {
        state.previousSelectedBlueprintId = state.selectedBlueprint.blueprint_id
      }
      state.selectedBlueprint = payload
    },
    updateSelectedBlueprint: (state, { payload }: PayloadAction<Partial<BlueprintV2>>) => {
      state.blueprints = state.blueprints.map((blueprint) => {
        if (blueprint.blueprint_id === state.selectedBlueprint?.blueprint_id) {
          return {
            ...blueprint,
            ...payload,
          }
        }
        return blueprint
      })

      if (state.selectedBlueprint) {
        state.selectedBlueprint = {
          ...state.selectedBlueprint,
          ...payload,
        }
      }
    },
    setIsCreatingBlueprint: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreatingBlueprint = payload
    },
    resetCreateBlueprint: (state, { payload }: PayloadAction<BlueprintV2 | undefined>) => {
      state.isCreatingBlueprint = false
      state.prevSelectedTool = state.selectedTool
      state.selectedTool = BlueprintEditorTools.Selector

      // Return to 2D mode after blueprint creation
      state.mode = Mode.Editor2D

      // we still need to set the previous selected blueprint id so a change of blueprint can be detected
      const previousId = state.previousSelectedBlueprintId
      state.previousSelectedBlueprintId = state.selectedBlueprint?.blueprint_id

      // If no payload is provided, we're not off from the creation flow
      if (!payload) {
        const previousBlueprint = state.blueprints.find((b) => b.blueprint_id === previousId)
        if (previousBlueprint) {
          state.selectedBlueprint = previousBlueprint
        } else if (state.blueprints.length > 0) {
          state.selectedBlueprint = getDefaultSelectedBlueprint(state.blueprints)
        } else {
          state.selectedBlueprint = undefined
        }

        return
      }

      // If payload is provided, we're from the creation flow, so add the new blueprint
      state.selectedBlueprint = payload
      state.blueprints.push(payload)
    },
    addDeletingBlueprintId: (state, { payload }: PayloadAction<string>) => {
      if (!state.deletingBlueprintIds.includes(payload)) {
        state.deletingBlueprintIds.push(payload)
      }
    },
    removeDeletingBlueprintId: (state, { payload }: PayloadAction<string>) => {
      state.deletingBlueprintIds = state.deletingBlueprintIds.filter((id) => id !== payload)
    },

    /**
     * PCD Settings
     */
    setDefaultPointSize: (state, { payload }: PayloadAction<number>) => {
      state.defaultPointSize = payload
    },
    setPointSize: (state, { payload }: PayloadAction<number>) => {
      state.pointSize = payload
    },
    setPcdTransparency: (state, { payload }: PayloadAction<number>) => {
      state.pcdTransparency = payload
      saveStateToLocalStorage('blueprintPcdTransparency', payload)
    },
    setBackgroundColor: (state, { payload }: PayloadAction<string>) => {
      state.backgroundColor = payload
      saveStateToLocalStorage('blueprintBackgroundColor', state.backgroundColor)
    },

    resetPointSize: (state) => {
      state.pointSize = state.defaultPointSize
    },
    resetPcdTransparency: (state) => {
      state.pcdTransparency = initialState.pcdTransparency
      deleteStateFromLocalStorage('blueprintPcdTransparency')
    },
    resetBackgroundColor: (state) => {
      state.backgroundColor = initialState.backgroundColor
      deleteStateFromLocalStorage('blueprintBackgroundColor')
    },

    /**
     * Panel collapse state
     */
    setCollapsedPanel: (state, { payload }: PayloadAction<PanelType[]>) => {
      state.collapsedPanel = payload
    },
    openPanel: (state, { payload }: PayloadAction<PanelType>) => {
      if (state.collapsedPanel.includes(payload)) {
        const index = state.collapsedPanel.indexOf(payload)
        state.collapsedPanel.splice(index, 1)
      }
    },
    toggleCollapsedPanel: (state, { payload }: PayloadAction<PanelType>) => {
      const index = state.collapsedPanel.indexOf(payload)
      if (index === -1) {
        state.collapsedPanel.push(payload)
      } else {
        state.collapsedPanel.splice(index, 1)
      }
    },
    forceOpenToolPanel: (state) => {
      if (state.collapsedPanel.includes(PanelType.Tool)) {
        state.collapsedPanel = state.collapsedPanel.filter((type) => type !== PanelType.Tool)
      }
    },

    /**
     * Reset working state.
     * Other than a few that does not change often, reset everything to initial state
     */
    resetWorkingState: (state) => ({
      ...initialState,
      permissionSet: state.permissionSet,
      isAllowedToModify: state.isAllowedToModify,

      pcdTransparency: state.pcdTransparency,
      backgroundColor: state.backgroundColor,
      gridOpacity: state.gridOpacity,
    }),

    reset: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      /**
       * ## Blueprints
       */
      .addCase(fetchBlueprints.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchBlueprints.fulfilled, (state, { payload }: PayloadAction<{ results: BlueprintV2[] }>) => {
        state.isLoading = false
        state.blueprints = payload.results
        state.selectedBlueprint = getDefaultSelectedBlueprint(payload.results)
      })
      .addCase(fetchBlueprints.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(updateBlueprint.fulfilled, (state, { payload }) => {
        const updatedBlueprint: BlueprintV2 = payload

        const index = state.blueprints.findIndex((blueprint) => blueprint.blueprint_id === payload.blueprint_id)
        if (index !== -1) {
          state.blueprints[index] = updatedBlueprint
        } else {
          state.blueprints.push(updatedBlueprint)
        }
        if (state.selectedBlueprint?.blueprint_id === payload.blueprint_id) {
          state.selectedBlueprint = updatedBlueprint
        }
      })
      .addCase(deleteBlueprint.fulfilled, (state, { meta: { arg } }) => {
        state.blueprints = state.blueprints.filter((blueprint) => blueprint.blueprint_id !== arg.blueprintId)
        if (state.selectedBlueprint?.blueprint_id === arg.blueprintId) {
          state.selectedBlueprint = getDefaultSelectedBlueprint(state.blueprints)
        }
        state.deletingBlueprintIds = state.deletingBlueprintIds.filter((id) => id !== arg.blueprintId)
      })
      .addCase(deleteBlueprint.rejected, (state, { meta: { arg } }) => {
        state.deletingBlueprintIds = state.deletingBlueprintIds.filter((id) => id !== arg.blueprintId)
      })
  },
})

export const {
  // Editor state
  setMode,
  setSelectedTool,
  setIsBatchSaving,

  setIsPointCloud2dLoading,
  setInitCompleted,
  setEditorLoadStartTime,
  setIsPointCloudInvisible,
  setGridOpacity,
  setPcdBoundingBox,
  setPcd2dBoundingBox,
  setPcdBoundingSphereRadius,

  // Permissions
  setPermissionSet,
  setIsAllowedModify,

  // Blueprints
  addBlueprint,
  removeBlueprint,
  setBlueprints,
  updateBlueprints,
  setSelectedBlueprint,
  updateSelectedBlueprint,
  setIsCreatingBlueprint,
  resetCreateBlueprint,
  addDeletingBlueprintId,
  removeDeletingBlueprintId,

  // PCD settings
  setDefaultPointSize,
  setPointSize,
  setPcdTransparency,
  setBackgroundColor,
  resetPointSize,
  resetPcdTransparency,
  resetBackgroundColor,

  // side panel
  setCollapsedPanel,
  openPanel,
  toggleCollapsedPanel,
  forceOpenToolPanel,

  resetWorkingState,
  reset,
} = slice.actions

export default combineReducers({
  editor: slice.reducer,
  drawings: drawingReducer,
  tools: combineReducers({
    textAnnotation: BlueprintToolTextAnnotation,
  }),
})
